import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getInvoice, isMedicineBill } from "../../core/api/invoiceService";
import { getPatient } from "../../core/api/patientService";
import { getDoctor } from "../../core/api/doctorService";
import { getTeeth } from "../../core/api/toothService";
import { getProcedures } from "../../core/api/procedureService";
import { CLINIC_INFO } from "../../core/constants/clinicInfo";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const lower = (v) => (v ? String(v).toLowerCase() : "");
const formatDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

const InvoicePrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [patient, setPatient] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [teethById, setTeethById] = useState(new Map());
  const [codesById, setCodesById] = useState(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getInvoice(id)
      .then(async (res) => {
        if (cancelled) return;
        const inv = res.data;
        setInvoice(inv);
        const patientId = inv.patientID ?? inv.patientId;
        const doctorId = inv.doctorID ?? inv.doctorId;
        const [patientRes, doctorRes, teethRes, proceduresRes] = await Promise.all([
          patientId ? getPatient(patientId).catch(() => null) : Promise.resolve(null),
          doctorId ? getDoctor(doctorId).catch(() => null) : Promise.resolve(null),
          getTeeth().catch(() => null),
          getProcedures().catch(() => null),
        ]);
        if (cancelled) return;
        if (patientRes) setPatient(patientRes.data);
        if (doctorRes) setDoctor(doctorRes.data);
        if (teethRes) setTeethById(new Map(teethRes.data.map((t) => [t.id, t.toothName])));
        // The procedure code lives on the Procedure master; invoice lines carry only the id.
        // GUIDs compare case-insensitively (see utils/ids).
        if (proceduresRes) setCodesById(new Map(proceduresRes.data.map((p) => [lower(p.id), p.procedureCode])));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (!invoice) return <div className="page-wrapper"><div className="content"><p>Invoice not found.</p></div></div>;

  // A procedure line can be charged against several teeth — render them comma-joined,
  // falling back to the API's current single toothID.
  const toothLabel = (l) => {
    const ids = Array.isArray(l.toothIDs ?? l.toothIds)
      ? (l.toothIDs ?? l.toothIds)
      : [l.toothID ?? l.toothId].filter(Boolean);
    const names = ids.map((t) => teethById.get(t)).filter(Boolean);
    return names.length ? names.join(", ") : "—";
  };

  const medicine = isMedicineBill(invoice);

  // The invoice has a single doctor, so every line is attributed to them.
  const doctorName = doctor?.doctorName || invoice.doctorName || "—";
  // Fee is the gross line charge; Payable is what remains after the line discount.
  const qtyOf = (l) => Number(l.qty ?? l.quantity ?? 1);
  const feeOf = (l) => qtyOf(l) * Number(l.rate || 0);
  // Medicine quantities matter on the bill, and there's no Qty column, so they go in the description.
  const withQty = (name, l) => (qtyOf(l) > 1 ? `${name} × ${qtyOf(l)}` : name);

  const rows = [
    ...(invoice.procedureLines || []).map((l) => ({
      tooth: toothLabel(l),
      code: l.procedureCode || codesById.get(lower(l.procedureID ?? l.procedureId)) || "—",
      description: withQty(l.procedureName || l.name || "Procedure", l),
      fee: feeOf(l),
      payable: Number(l.amount || 0),
    })),
    ...(invoice.productLines || []).map((l) => ({
      tooth: "—",
      code: "—",
      description: withQty(l.itemName || l.productName || l.name || "Product", l),
      fee: feeOf(l),
      payable: Number(l.amount || 0),
    })),
  ];
  const totalFee = rows.reduce((s, r) => s + r.fee, 0);
  const totalPayable = rows.reduce((s, r) => s + r.payable, 0);
  const overallDiscount = Number(invoice.overallDiscount || invoice.discount || 0);
  const taxAmount = Number(invoice.taxAmount || 0);

  const payments = invoice.payments || [];
  const paymentModes = [...new Set(payments.map((p) => p.paymentMode || "Cash"))].join(", ") || "—";
  // Receipt numbers are shown once the API returns one per payment.
  const receiptNos = payments.map((p) => p.receiptNo || p.receiptNumber).filter(Boolean).join(", ");

  return (
    <div className="page-wrapper invoice-print-page">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .header, #sidebar, .page-header, .no-print { display: none !important; }
          .page-wrapper { margin: 0 !important; padding: 0 !important; }
          .invoice-print-area { box-shadow: none !important; min-height: 100vh; }
          body { background: #fff !important; }
        }
        .invoice-print-area { max-width: 820px; margin: 0 auto; background: #fff; padding: 40px; font-family: Arial, sans-serif; color: #000; display: flex; flex-direction: column; }
        .invoice-print-area .bill-info { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 28px; }
        .invoice-print-area .bill-info table td { padding: 2px 0; vertical-align: top; }
        .invoice-print-area .bill-info table td:first-child { padding-right: 10px; white-space: nowrap; }
        .invoice-print-area .bill-info .patient-name { font-weight: bold; }
        .invoice-print-area .lines-table { width: 100%; border-collapse: collapse; }
        .invoice-print-area .lines-table th { font-weight: bold; text-align: left; padding: 8px 8px; border-top: 2px solid #000; border-bottom: 2px solid #000; }
        .invoice-print-area .lines-table td { padding: 8px 8px; vertical-align: top; }
        .invoice-print-area .lines-table .num { text-align: right; }
        .invoice-print-area .lines-table .total-row td { font-weight: bold; }
        .invoice-print-area .lines-table .total-row td.num { border-top: 2px solid #000; }
        .invoice-print-area .bill-bottom { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 24px; gap: 24px; }
        .invoice-print-area .summary { border-collapse: collapse; min-width: 380px; }
        .invoice-print-area .summary td { padding: 4px 8px; }
        .invoice-print-area .summary td:last-child { text-align: right; font-weight: bold; }
        .invoice-print-area .payment-mode span { margin-left: 40px; font-style: italic; }
        .invoice-print-area .print-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
        .invoice-print-area .print-logo { max-height: 70px; }
        .invoice-print-area .print-rule { border: none; border-top: 2px solid #000; margin: 0 0 24px; }
        .invoice-print-area .print-footer { margin-top: auto; padding-top: 40px; font-size: 0.82em; color: #333; }
        .invoice-print-area .print-footer-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .invoice-print-area .print-footer-dots span { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: 4px; }
        .invoice-print-area .print-footer-strapline { font-style: italic; color: #555; }
        .invoice-print-area .print-footer-rule { border: none; border-top: 1px solid #999; margin: 0 0 8px; }
        .invoice-print-area .print-footer-bottom { display: flex; justify-content: space-between; align-items: flex-end; }
        .invoice-print-area .print-footer-bottom .print-footer-right { text-align: right; }
        .invoice-print-area .print-footer .clinic-name-footer { font-weight: bold; font-size: 1.05em; color: #000; margin-bottom: 2px; }
      `}</style>

      <div className="content no-print d-flex justify-content-end gap-2 mb-3">
        <button
          className="btn btn-cancel"
          onClick={() => {
            // Print is always opened via window.open(..., "_blank") from the invoice
            // list/form, so this tab has no in-app history to go back to — close the
            // tab if it was opened that way, otherwise fall back to the invoice list.
            if (window.opener) window.close();
            else navigate(medicine ? "/medicine-bills" : "/invoices");
          }}
        >
          Back
        </button>
        <button className="btn btn-submit" onClick={() => window.print()}>Print</button>
      </div>

      <div className="invoice-print-area">
        <div className="print-header">
          <img src={CLINIC_INFO.logo} alt={CLINIC_INFO.clinicName} className="print-logo" />
        </div>
        <hr className="print-rule" />

        {medicine && (
          <h5 style={{ textAlign: "center", fontWeight: "bold", margin: "0 0 16px" }}>MEDICINE BILL</h5>
        )}

        <div className="bill-info">
          <table>
            <tbody>
              <tr>
                <td>Patient name</td>
                <td className="patient-name">: {patient?.patientName || invoice.patientName || "—"}</td>
              </tr>
              <tr>
                <td>Reg No</td>
                <td>: {patient?.opNumber || "—"}</td>
              </tr>
              <tr>
                <td>Address</td>
                <td>: {patient?.address || "—"}</td>
              </tr>
            </tbody>
          </table>
          <table>
            <tbody>
              <tr>
                <td>Invoice No</td>
                <td>: {invoice.invoiceNumber || "—"}</td>
              </tr>
              <tr>
                <td>Date</td>
                <td>: {formatDate(invoice.invoiceDate)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <table className="lines-table">
          <thead>
            <tr>
              <th style={{ width: 55 }}>Sl No</th>
              <th style={{ width: 80 }}>Tooth</th>
              <th style={{ width: 120 }}>Doctor</th>
              <th style={{ width: 70 }}>Code</th>
              <th>Description</th>
              <th className="num" style={{ width: 100 }}>Fee</th>
              <th className="num" style={{ width: 100 }}>Payable</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center" }}>No line items</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>{r.tooth}</td>
                <td>{doctorName}</td>
                <td>{r.code}</td>
                <td>{r.description}</td>
                <td className="num">{fmt(r.fee)}</td>
                <td className="num">{fmt(r.payable)}</td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="total-row">
                <td colSpan={5}></td>
                <td className="num">{fmt(totalFee)}</td>
                <td className="num">{fmt(totalPayable)}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="bill-bottom">
          <div className="payment-mode">Mode of payment:<span>{paymentModes}</span></div>
          <table className="summary">
            <tbody>
              <tr>
                <td>Payable</td>
                <td>{fmt(totalPayable)}</td>
              </tr>
              {overallDiscount > 0 && (
                <tr>
                  <td>Discount</td>
                  <td>{fmt(overallDiscount)}</td>
                </tr>
              )}
              {taxAmount > 0 && (
                <tr>
                  <td>Tax</td>
                  <td>{fmt(taxAmount)}</td>
                </tr>
              )}
              <tr>
                <td>Net Payable</td>
                <td>{fmt(invoice.netTotal)}</td>
              </tr>
              <tr>
                <td>Paid{receiptNos ? ` (Receipt No:${receiptNos})` : ""}</td>
                <td>{fmt(invoice.paidAmount)}</td>
              </tr>
              <tr>
                <td>Balance due</td>
                <td>{fmt(invoice.balanceAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="print-footer">
          <div className="print-footer-top">
            <div className="print-footer-dots">
              <span style={{ background: "#f2c94c" }} />
              <span style={{ background: "#e29b9b" }} />
              <span style={{ background: "#8fbf8f" }} />
              <span style={{ background: "#8fb8d9" }} />
            </div>
            <div className="print-footer-strapline">{CLINIC_INFO.strapline}</div>
          </div>
          <hr className="print-footer-rule" />
          <div className="print-footer-bottom">
            <div className="print-footer-left">
              <div className="clinic-name-footer">{CLINIC_INFO.clinicName}</div>
              <div>{CLINIC_INFO.tagline}</div>
              <div>{CLINIC_INFO.addressLines.join(", ")}</div>
            </div>
            <div className="print-footer-right">
              <div>{CLINIC_INFO.website}</div>
              <div>{CLINIC_INFO.phones.join(" | ")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrint;
